import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import "../store/Store.css";

const POLICY_IDS = ["shipping", "returns", "privacy", "terms"];
const POLICY_ICONS = {
  shipping: "fa-solid fa-truck",
  returns:  "fa-solid fa-rotate-left",
  privacy:  "fa-solid fa-shield-halved",
  terms:    "fa-solid fa-file-contract",
};

function normalizePolicyId(raw) {
  const id = String(raw || "").toLowerCase().trim();
  return POLICY_IDS.includes(id) ? id : "shipping";
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function lineToWordHtml(line) {
  const isHeading = line.startsWith("## ");
  const text = isHeading ? line.slice(3) : line;
  const inline = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return isHeading
    ? `<h2 style="font-size:15pt;color:#2a2520;margin:18pt 0 6pt;">${inline}</h2>`
    : `<p style="font-size:11pt;line-height:1.7;margin:6pt 0;color:#3a342c;">${inline}</p>`;
}

function downloadPolicyAsWord(title, lines, isRtl, filenameBase) {
  const dir = isRtl ? "rtl" : "ltr";
  const align = isRtl ? "right" : "left";
  const bodyHtml = lines.map(lineToWordHtml).join("\n");
  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<title>${title}</title>
</head>
<body dir="${dir}" style="font-family:Arial, Tahoma, sans-serif; text-align:${align};">
<h1 style="font-size:20pt; color:#1e1a16; margin-bottom:10pt;">${title}</h1>
${bodyHtml}
</body>
</html>`;
  const blob = new Blob(["﻿", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function StorePolicies() {
  const { policyId } = useParams();
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const lang = isRtl ? "ar" : "en";
  const id = normalizePolicyId(policyId);

  const [policiesMap, setPoliciesMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get("/store/policies")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((p) => { map[p.policyId] = p; });
        setPoliciesMap(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const current = policiesMap[id];
  const title = current?.[lang]?.title || t(`store.policies.${id}.title`, { defaultValue: id });
  const bodyRaw = current?.[lang]?.body || "";
  const updatedAt = current?.updatedAt;

  const bodyLines = useMemo(
    () =>
      String(bodyRaw || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    [bodyRaw]
  );

  const navItems = useMemo(
    () =>
      POLICY_IDS.map((pid) => {
        const p = policiesMap[pid];
        const navTitle = p?.[lang]?.title || t(`store.policies.${pid}.title`, { defaultValue: pid });
        return { id: pid, title: navTitle };
      }),
    [policiesMap, lang, t]
  );

  return (
    <section className="store-page store-policy-page" dir={isRtl ? "rtl" : "ltr"} lang={isRtl ? "ar" : "en"}>
      <div className="container">
        <div className="store-policy-shell">
          <Link to="/store" className="store-checkout-back store-policy-back">
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>{t("store.policies.backToStore", { defaultValue: "Back to store" })}</span>
          </Link>

          <div className="store-policy-grid">
            <aside className="store-policy-nav" aria-label={t("store.policies.navAria", { defaultValue: "Policies" })}>
              <h2 className="store-policy-nav-title">
                {t("store.policies.title", { defaultValue: "Store policies" })}
              </h2>
              <ul className="store-policy-nav-list">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/store/policies/${item.id}`}
                      className={`store-policy-nav-link${item.id === id ? " is-active" : ""}`}
                    >
                      <i className={POLICY_ICONS[item.id]} aria-hidden="true" />
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>

            <article className="store-policy-card" aria-labelledby="store-policy-title">
              <h1 id="store-policy-title" className="store-policy-title">{title}</h1>
              <div className="store-policy-header-row">
                {updatedAt && (
                  <p className="store-policy-updated">
                    <i className="fa-regular fa-clock" aria-hidden="true" />
                    {isRtl ? "آخر تحديث:" : "Last updated:"} {formatDate(updatedAt)}
                  </p>
                )}
                {!loading && bodyLines.length > 0 && (
                  <button
                    type="button"
                    className="store-policy-download-btn"
                    onClick={() => downloadPolicyAsWord(title, bodyLines, isRtl, id)}
                  >
                    <i className="fa-solid fa-file-word" aria-hidden="true" />
                    {t("store.policies.downloadWord", { defaultValue: "Download as Word" })}
                  </button>
                )}
              </div>

              {loading ? (
                <div className="store-policy-loading">
                  <i className="fa-solid fa-circle-notch fa-spin" />
                </div>
              ) : (
                <div className="store-policy-body">
                  {bodyLines.length === 0 ? (
                    <p className="store-policy-body-para">
                      {t("store.policies.noContent", { defaultValue: "Content coming soon." })}
                    </p>
                  ) : bodyLines.map((line, idx) => {
                    const isHeading = line.startsWith("## ");
                    const text = isHeading ? line.slice(3) : line;
                    return (
                      <p
                        key={`${id}-${idx}`}
                        className={isHeading ? "store-policy-body-heading" : "store-policy-body-para"}
                      >
                        {renderInline(text)}
                      </p>
                    );
                  })}
                </div>
              )}
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
